import { format, type Locale } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { enUS, lt, ru } from 'date-fns/locale';
import type { Language } from './enum';

const localeMap: Record<Language, Locale> = {
    EN: enUS,
    LT: lt,
    RU: ru,
};

export interface Countdown {
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
}

export interface Duration {
    hours: number;
    minutes: number;
    seconds: number;
}

export function formatUtcToLocal(dateStr: string | null | undefined, formatStr: string): string {
    if (!dateStr) {
        return 'N/A';
    }

    // Ensure the date is treated as UTC by appending 'Z' if no timezone is specified
    const utcDateStr = dateStr.match(/[TZ]/) ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateStr);
    if (isNaN(date.getTime())) {
        return 'N/A';
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zonedDate = toZonedTime(date, timeZone);
    return format(zonedDate, convertMomentToDateFnsFormat(formatStr));
}

export function formatUtcToLocalWithLocale(
    dateStr: string | null | undefined,
    formatStr: string,
    language: Language,
): string {
    if (!dateStr) {
        return 'N/A';
    }

    // Ensure the date is treated as UTC by appending 'Z' if no timezone is specified
    const utcDateStr = dateStr.match(/[TZ]/) ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateStr);
    if (isNaN(date.getTime())) {
        return 'N/A';
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = localeMap[language];
    const zonedDate = toZonedTime(date, timeZone);
    return format(zonedDate, convertMomentToDateFnsFormat(formatStr), { locale });
}

export function formatUtcToLocalDate(date: Date, formatStr: string): string {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zonedDate = toZonedTime(date, timeZone);
    return format(zonedDate, convertMomentToDateFnsFormat(formatStr));
}

export function getDurationBetween(startStr: string, endStr: string): Duration {
    const startDate = new Date(startStr);
    const endDate = new Date(endStr);

    const diffMs = endDate.getTime() - startDate.getTime();
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const totalSeconds = Math.floor(diffMs / 1000);
    const seconds = totalSeconds % 60; // seconds part (0–59)

    return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
        seconds: seconds,
    };
}

export function getCountdown(targetStr: string): Countdown {
    const target = new Date(targetStr);
    const now = new Date();

    const diffMs = target.getTime() - now.getTime();
    const totalSeconds = Math.floor(diffMs / 1000);

    if (totalSeconds <= 0) {
        return { hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 };
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { hours, minutes, seconds, totalSeconds };
}

export function toUtcIsoString(dateStr: string | null | undefined): string | null {
    if (!dateStr) {
        return null;
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
        return null;
    }

    return date.toISOString();
}

export function getSecondsDiff(targetStr: string | undefined, referenceDate?: Date): number {
    if (!targetStr) {
        return 0;
    }
    const target = new Date(targetStr);
    const now = referenceDate || new Date();

    return Math.floor((target.getTime() - now.getTime()) / 1000);
}

export function convertMomentToDateFnsFormat(momentFormat: string): string {
    const mapping: Record<string, string> = {
        YYYY: 'yyyy',
        MM: 'MM',
        DD: 'dd',
        'MMM.': 'MMM',
        MMMM: 'MMMM',
        'HH:mm': 'HH:mm',
        'HH:MM': 'HH:mm',
        D: 'd',
    };

    let result = momentFormat;
    for (const [momentToken, dateFnsToken] of Object.entries(mapping)) {
        result = result.replace(new RegExp(momentToken, 'g'), dateFnsToken);
    }

    return result;
}

export interface DurationWithText {
    days: number;
    hours: number;
    minutes: number;
}

export function parseDurationString(durationStr: string | null | undefined): DurationWithText | null {
    if (!durationStr) {
        return null;
    }

    const trimmed = durationStr.trim();

    // Format: "DD HH:MM:SS" (e.g., "05 08:32:08") - days + time
    const withDaysMatch = trimmed.match(/^(\d+)\s+(\d{1,2}):(\d{2}):(\d{2})$/);
    if (withDaysMatch) {
        return {
            days: parseInt(withDaysMatch[1], 10),
            hours: parseInt(withDaysMatch[2], 10),
            minutes: parseInt(withDaysMatch[3], 10),
        };
    }

    // Format: "HH:MM:SS" (e.g., "08:32:08" or "3:44:09") - hours:minutes:seconds
    const withSecondsMatch = trimmed.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
    if (withSecondsMatch) {
        return {
            days: 0,
            hours: parseInt(withSecondsMatch[1], 10),
            minutes: parseInt(withSecondsMatch[2], 10),
        };
    }

    // Format: "HH:MM" (e.g., "08:32" or "44:45") - hours:minutes
    const hoursMinutesMatch = trimmed.match(/^(\d{1,3}):(\d{2})$/);
    if (hoursMinutesMatch) {
        const hours = parseInt(hoursMinutesMatch[1], 10);
        const minutes = parseInt(hoursMinutesMatch[2], 10);
        // Validate minutes are less than 60
        if (minutes < 60) {
            return {
                days: 0,
                hours,
                minutes,
            };
        }
    }

    // Format: just minutes as a number (e.g., "32" or "5")
    const justMinutesMatch = trimmed.match(/^(\d+)$/);
    if (justMinutesMatch) {
        return {
            days: 0,
            hours: 0,
            minutes: parseInt(justMinutesMatch[1], 10),
        };
    }

    return null;
}

export function formatDurationWithText(
    durationStr: string | null | undefined,
    daysAbbr: string,
    hoursAbbr: string,
    minutesAbbr: string,
): string {
    const duration = parseDurationString(durationStr);
    if (!duration) {
        return '-';
    }

    const parts: string[] = [];

    if (duration.days > 0) {
        parts.push(`${duration.days}${daysAbbr}`);
    }
    if (duration.hours > 0 || duration.days > 0) {
        parts.push(`${duration.hours}${hoursAbbr}`);
    }
    if (duration.minutes > 0 || (duration.days === 0 && duration.hours === 0)) {
        parts.push(`${duration.minutes}${minutesAbbr}`);
    }

    return parts.join(' ') || '-';
}
